import { useState } from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { Eye, EyeSlash, ArrowsClockwise, Copy, Check, EnvelopeSimple, Key } from "@phosphor-icons/react";
import { copyText } from "../lib/tenantPortal";

export const ADMIN_ROLE_OPTIONS = [
  { key: "super_admin", label: "Super Admin", blurb: "Owns the workspace: billing, users, every module" },
  { key: "operations_director", label: "Operations Director", blurb: "Full day-to-day access (locked system role) without owner-level billing" },
];

export const EMPTY_ADMIN = { name: "", email: "", role: "super_admin", mode: "invite", password: "" };

const genPassword = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!#$%";
  const arr = new Uint32Array(14);
  window.crypto.getRandomValues(arr);
  return Array.from(arr, (n) => chars[n % chars.length]).join("");
};

// Platform console → New Tenant: the workspace's first admin (role + how they get in)
export function TenantAdminFields({ value, onChange, title = "First admin", subtitle = "Optional — leave the email blank to create an empty workspace and add people later." }) {
  const [showPw, setShowPw] = useState(false);
  const set = (patch) => onChange({ ...value, ...patch });
  return (
    <div className="border border-border rounded-md p-3 space-y-3 bg-muted/20" data-testid="tenant-admin-fields">
      <div>
        <p className="text-xs font-semibold">{title}</p>
        <p className="text-[10px] text-muted-foreground">{subtitle}</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Name</Label>
          <Input className="mt-1" value={value.name} onChange={(e) => set({ name: e.target.value })} placeholder="Jane Smith" data-testid="tenant-admin-name" />
        </div>
        <div>
          <Label className="text-xs">Email</Label>
          <Input className="mt-1" type="email" value={value.email} onChange={(e) => set({ email: e.target.value })} placeholder="jane@company.co.uk" data-testid="tenant-admin-email" />
        </div>
      </div>
      <div>
        <Label className="text-xs">Admin role</Label>
        <Select value={value.role} onValueChange={(v) => set({ role: v })}>
          <SelectTrigger className="mt-1" data-testid="tenant-admin-role"><SelectValue /></SelectTrigger>
          <SelectContent>
            {ADMIN_ROLE_OPTIONS.map((r) => (
              <SelectItem key={r.key} value={r.key} data-testid={`tenant-admin-role-${r.key}`}>{r.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-[10px] text-muted-foreground mt-1">{ADMIN_ROLE_OPTIONS.find((r) => r.key === value.role)?.blurb}</p>
      </div>
      <div>
        <Label className="text-xs">How do they get in?</Label>
        <div className="grid grid-cols-2 gap-2 mt-1">
          {[{ k: "invite", icon: EnvelopeSimple, t: "Send a link", d: "They pick their own password" }, { k: "password", icon: Key, t: "Set a password", d: "You give them the password" }].map(({ k, icon: Icon, t, d }) => (
            <button type="button" key={k} onClick={() => set({ mode: k, password: k === "password" && !value.password ? genPassword() : value.password })}
              className={`text-left rounded-md border px-3 py-2 transition-colors ${value.mode === k ? "border-primary bg-primary/5" : "border-border hover:bg-muted/40"}`}
              data-testid={`tenant-admin-mode-${k}`} aria-pressed={value.mode === k}>
              <p className="text-xs font-semibold flex items-center gap-1.5"><Icon size={13} /> {t}</p>
              <p className="text-[10px] text-muted-foreground">{d}</p>
            </button>
          ))}
        </div>
      </div>
      {value.mode === "password" && (
        <div>
          <Label className="text-xs">Password</Label>
          <div className="flex gap-1.5 mt-1">
            <Input type={showPw ? "text" : "password"} value={value.password} onChange={(e) => set({ password: e.target.value })} minLength={8} className="flex-1 font-mono" data-testid="tenant-admin-password" />
            <Button type="button" variant="outline" size="icon" onClick={() => setShowPw((s) => !s)} data-testid="tenant-admin-password-toggle" aria-label={showPw ? "Hide password" : "Show password"}>
              {showPw ? <EyeSlash size={14} /> : <Eye size={14} />}
            </Button>
            <Button type="button" variant="outline" size="icon" onClick={() => set({ password: genPassword() })} data-testid="tenant-admin-password-generate" aria-label="Generate password"><ArrowsClockwise size={14} /></Button>
          </div>
          <p className="text-[10px] text-muted-foreground mt-1">At least 8 characters. Share it with them securely — it isn't e-mailed.</p>
        </div>
      )}
    </div>
  );
}

// Shown after a successful create: where they sign in, and the invite link if one was issued
export function TenantAdminResult({ result, onClose }) {
  const [copied, setCopied] = useState("");
  const copy = async (key, text) => { await copyText(text); setCopied(key); toast.success("Copied"); setTimeout(() => setCopied(""), 1500); };
  if (!result) return null;
  const a = result.admin;
  return (
    <div className="space-y-3 min-w-0" data-testid="tenant-created-result">
      <p className="text-sm">Workspace <strong>{result.name}</strong> is ready{a ? <> and <strong>{a.name}</strong> is its {a.role_label}.</> : "."}</p>
      {a && (
        <div className="rounded-md border border-border p-3 space-y-2 text-xs min-w-0">
          <div className="flex items-start justify-between gap-2 min-w-0">
            <span className="text-muted-foreground shrink-0">Sign-in page</span>
            <span className="flex items-start gap-1.5 font-mono break-all text-right min-w-0" data-testid="tenant-created-login-url">{a.login_url}
              <button type="button" onClick={() => copy("login", a.login_url)} className="text-muted-foreground hover:text-foreground shrink-0 mt-0.5" data-testid="tenant-created-copy-login">{copied === "login" ? <Check size={12} /> : <Copy size={12} />}</button>
            </span>
          </div>
          {a.mode === "invite" ? (
            <>
              <div className="flex items-start justify-between gap-2 min-w-0">
                <span className="text-muted-foreground shrink-0">Invite link</span>
                <span className="flex items-start gap-1.5 font-mono break-all text-right min-w-0" data-testid="tenant-created-invite-url">{a.invite_url}
                  <button type="button" onClick={() => copy("invite", a.invite_url)} className="text-muted-foreground hover:text-foreground shrink-0 mt-0.5" data-testid="tenant-created-copy-invite">{copied === "invite" ? <Check size={12} /> : <Copy size={12} />}</button>
                </span>
              </div>
              <p className="text-[10px] text-muted-foreground" data-testid="tenant-created-email-status">{a.email_sent ? `Invite e-mailed to ${a.email}.` : `E-mail could not be sent — share the invite link with ${a.email} yourself.`}</p>
              {!a.email_sent && a.email_error && <p className="text-[10px] text-red-600 dark:text-red-400 break-words" data-testid="tenant-created-email-error">Mail server said: {a.email_error}. Check Platform → E-mail Health.</p>}
            </>
          ) : (
            <>
              <p className="text-[10px] text-muted-foreground" data-testid="tenant-created-email-status">{a.email_sent ? `Welcome e-mail sent to ${a.email} (the password is not included — share it securely).` : `Welcome e-mail could not be sent — tell ${a.email} where to sign in and share the password securely.`}</p>
              {!a.email_sent && a.email_error && <p className="text-[10px] text-red-600 dark:text-red-400 break-words" data-testid="tenant-created-email-error">Mail server said: {a.email_error}. Check Platform → E-mail Health.</p>}
            </>
          )}
        </div>
      )}
      <Button className="w-full" onClick={onClose} data-testid="tenant-created-done">Done</Button>
    </div>
  );
}
